import React, { useState } from "react";
import {
  ArrowLeft,
  Sun,
  Moon,
  Shield,
  Lock,
  Eye,
  Database,
  Globe,
  UserCheck,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";

const PrivacyPolicy = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
      }`}
    >
      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 border-b ${
          isDarkMode
            ? "bg-gray-900/95 backdrop-blur-md border-gray-800"
            : "bg-white/95 backdrop-blur-md border-gray-200"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.history.back()}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"
                }`}
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center space-x-3">
                <Shield className="w-8 h-8 text-[#FEF200]" />
                <h1 className="text-xl font-semibold">Privacy Policy</h1>
              </div>
            </div>

            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-colors ${
                isDarkMode
                  ? "bg-gray-800 text-[#FEF200] hover:bg-gray-700"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div
            className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-6 ${
              isDarkMode ? "bg-gray-800" : "bg-gray-100"
            }`}
          >
            <Lock className="w-5 h-5 text-[#FEF200]" />
            <span className="text-sm font-medium">
              Last Updated: August 14, 2025
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-semibold mb-6">
            Privacy Policy
          </h1>
          <p
            className={`text-lg leading-relaxed max-w-3xl mx-auto ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            This Privacy Policy explains how Homeegram ("we", "us", "our")
            collects, uses, shares & protects your personal data when you use
            the Homeegram app & related services.
          </p>
        </div>

        {/* Contact Information */}
        <div
          className={`rounded-2xl p-8 mb-12 ${
            isDarkMode
              ? "bg-gradient-to-r from-gray-800 to-gray-700"
              : "bg-gradient-to-r from-blue-50 to-indigo-50"
          }`}
        >
          <h3 className="text-xl font-semibold mb-6 flex items-center">
            <UserCheck className="w-6 h-6 mr-3 text-[#FEF200]" />
            Contact & Grievance Officer
          </h3>
          <div className="grid md:grid-cols-1 gap-6">
            <div>
              <p className="font-semibold mb-3">
                Privacy & Grievance Officer: Abijith Biju K
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <Mail className="w-4 h-4 text-[#FEF200]" />
                  <span>grievance@homeegram.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-4 h-4 text-[#FEF200]" />
                  <span>abijith@homeeera.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-4 h-4 text-[#FEF200]" />
                  <span>+91 95395 61999</span>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-4 h-4 text-[#FEF200] mt-1" />
                  <span>
                    NH 544, First Floor, G.I Square Opposite Arya Bhangy Motors,
                    Kothakulangara, Angamaly, Ernakulam, Kerala 683572, India
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div
          className={`rounded-2xl p-6 mb-12 ${
            isDarkMode ? "bg-gray-800" : "bg-gray-50"
          }`}
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Database className="w-5 h-5 mr-2 text-[#FEF200]" />
            Quick Navigation
          </h3>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              "1. Scope",
              "2. Categories of Personal Data",
              "3. How We Collect Data",
              "4. Purposes & Lawful Bases",
              "5. Sharing & Third Parties",
              "6. Account Deletion & Retention",
              "7. Cookies & Technologies",
              "8. Permissions",
              "9. Security Measures",
              "10. Your Rights",
              "11. Children's Privacy",
              "12. International Transfers",
              "13. App Store & Data Safety",
              "14. Changes to Policy",
            ].map((item, index) => (
              <a
                key={index}
                href={`#privacy-section-${index + 1}`}
                className={`text-sm py-2 px-3 rounded-lg transition-colors ${
                  isDarkMode
                    ? "hover:bg-gray-700 text-gray-300 hover:text-white"
                    : "hover:bg-white text-gray-600 hover:text-gray-900"
                }`}
              >
                {item}
              </a>
            ))}
          </div>
        </div>

        {/* Privacy Policy Content */}
        <div className="space-y-12">
          {/* Section 1 - Scope */}
          <section id="privacy-section-1" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                1
              </div>
              Scope
            </h2>
            <p>
              This Privacy Policy applies to data collected via the Homeegram
              mobile app, website & customer support channels.
            </p>
          </section>

          {/* Section 2 - Categories of Personal Data */}
          <section id="privacy-section-2" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                2
              </div>
              Categories of Personal Data Collected
            </h2>
            <div className="space-y-6">
              <p>We collect data depending on features used:</p>

              <div className="grid gap-4">
                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2">Account & Identity</h4>
                  <p className="text-sm">
                    Name, username, profile photo, business name, verification
                    documents (IDs) where provided.
                  </p>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2">Contact Information</h4>
                  <p className="text-sm">
                    Email, mobile number, postal address.
                  </p>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2">Professional & Profile</h4>
                  <p className="text-sm">
                    Profession, qualifications, portfolio photos, service
                    listings, reviews.
                  </p>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2">
                    Transactional & Payment
                  </h4>
                  <p className="text-sm">
                    Payment tokens, invoices, payout bank/UPI details (if you
                    receive payouts). Card PAN/full numbers are not stored by us
                    (handled by PSPs).
                  </p>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2">
                    Communications & Support
                  </h4>
                  <p className="text-sm">Messages, support transcripts.</p>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2">Device & Usage</h4>
                  <p className="text-sm">
                    Device identifiers, IP address, OS, crash reports, analytics
                    events.
                  </p>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2">Location Data</h4>
                  <p className="text-sm">
                    Approximate (IP) or precise only if you permit device
                    location.
                  </p>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2">User Content</h4>
                  <p className="text-sm">
                    Photos, videos, textual posts you upload.
                  </p>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h4 className="font-semibold mb-2">Marketing & Preferences</h4>
                  <p className="text-sm">
                    Opt-ins & engagement data.
                  </p>
                </div>
              </div>

              <div
                className={`p-6 rounded-lg border-l-4 border-yellow-400 ${
                  isDarkMode ? "bg-gray-800" : "bg-yellow-50"
                }`}
              >
                <h4 className="font-semibold mb-2">
                  Special Categories / Sensitive Data
                </h4>
                <p className="text-sm">
                  Government ID images for verification are processed with extra
                  care — only when necessary & with your consent.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3 - How We Collect Data */}
          <section id="privacy-section-3" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                3
              </div>
              How We Collect Data
            </h2>
            <div className="space-y-4">
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  Directly from you (registration, profile, posts, purchases)
                </li>
                <li>Automatically (device, usage, cookies, analytics SDKs)</li>
                <li>
                  From third parties (payment providers, identity verification
                  services, public sources)
                </li>
              </ul>
            </div>
          </section>

          {/* Section 4 - Purposes & Lawful Bases */}
          <section id="privacy-section-4" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                4
              </div>
              Purposes & Lawful Bases
            </h2>
            <div className="space-y-4">
              <p>We process data to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide the Services</li>
                <li>Process payments & refunds</li>
                <li>Communicate with users</li>
                <li>Perform KYC/anti-fraud measures</li>
                <li>
                  Personalise content & adverts (with consent where required)
                </li>
                <li>Comply with legal obligations</li>
              </ul>
              <p>
                Lawful bases include contractual necessity, legal obligation,
                consent & legitimate interests as appropriate under applicable
                law (DPDP Act).
              </p>
            </div>
          </section>

          {/* Section 5 - Sharing & Third Parties */}
          <section id="privacy-section-5" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                5
              </div>
              Sharing & Third Parties
            </h2>
            <div className="space-y-6">
              <p>We share data with:</p>

              <div className="space-y-4">
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    Payment processors, banks, PSPs (for payments/payouts)
                  </li>
                  <li>Analytics & crash reporting providers (Firebase)</li>
                  <li>
                    Advertising & measurement partners (AdMob, AppsFlyer, Meta)
                    for ads/measurement where consented
                  </li>
                  <li>Cloud hosting & support vendors</li>
                  <li>Law enforcement/regulators if required</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3">
                  Additional Third-Party Services
                </h3>

                <div className="grid gap-4">
                  <div
                    className={`p-4 rounded-lg ${
                      isDarkMode ? "bg-gray-800" : "bg-gray-50"
                    }`}
                  >
                    <h4 className="font-semibold mb-2">Payment Processing</h4>
                    <p className="text-sm">
                      Razorpay (SDK version 1.6.40+) - Payment gateway for
                      transaction processing, KYC verification, & secure
                      payment handling.
                    </p>
                  </div>

                  <div
                    className={`p-4 rounded-lg ${
                      isDarkMode ? "bg-gray-800" : "bg-gray-50"
                    }`}
                  >
                    <h4 className="font-semibold mb-2">
                      Mapping & Location Services
                    </h4>
                    <p className="text-sm">
                      Ola Maps API (version 0.0.7) - Location services,
                      geocoding, routing functionality, & map-based features.
                    </p>
                  </div>

                  <div
                    className={`p-4 rounded-lg ${
                      isDarkMode ? "bg-gray-800" : "bg-gray-50"
                    }`}
                  >
                    <h4 className="font-semibold mb-2">
                      Error Monitoring & Performance
                    </h4>
                    <p className="text-sm">
                      Sentry SDK (version 2.34.1) - Crash reporting, error
                      tracking, performance monitoring, & application
                      stability analysis.
                    </p>
                  </div>

                  <div
                    className={`p-4 rounded-lg ${
                      isDarkMode ? "bg-gray-800" : "bg-gray-50"
                    }`}
                  >
                    <h4 className="font-semibold mb-2">Search Services</h4>
                    <p className="text-sm">
                      Meile Search - Enhanced search functionality within the
                      application for improved user experience.
                    </p>
                  </div>

                  <div
                    className={`p-4 rounded-lg ${
                      isDarkMode ? "bg-gray-800" : "bg-gray-50"
                    }`}
                  >
                    <h4 className="font-semibold mb-2">Cloud Infrastructure</h4>
                    <ul className="text-sm space-y-1">
                      <li>
                        • Amazon Web Services (AWS) - Cloud hosting, data
                        storage, content delivery, & backend infrastructure
                        services
                      </li>
                      <li>
                        • Bunny CDN (version 0.0.14) - Content delivery network
                        for media files, image optimization, & faster content
                        loading
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className="text-sm">
                These services process data as described in this Privacy Policy
                & are bound by their respective privacy policies & data
                protection agreements with us. We contractually require third
                parties to protect data & process only per our instructions.
              </p>
            </div>
          </section>

          {/* Section 6 - Account Deletion & Data Retention */}
          <section id="privacy-section-6" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                6
              </div>
              Account Deletion & Data Retention
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-3">
                  How to Delete Your Account
                </h3>
                <p className="mb-4">
                  In-app: Settings → Account → Delete Account
                </p>
                <p>
                  After confirmation, account will be disabled immediately;
                  personal data will be deleted within 30 days except for
                  limited records we must retain for legal/tax/fraud prevention.
                  Backups may remain for up to 180 days. Contributions already
                  shared with others may persist.
                </p>
              </div>

              <div
                className={`p-4 rounded-lg border-l-4 border-yellow-400 ${
                  isDarkMode ? "bg-gray-800" : "bg-yellow-50"
                }`}
              >
                <p className="text-sm">
                  For assistance with account deletion, email
                  grievance@homeegram.com
                </p>
              </div>
            </div>
          </section>

          {/* Section 7 - Cookies & Similar Technologies */}
          <section id="privacy-section-7" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                7
              </div>
              Cookies & Similar Technologies
            </h2>
            <div className="space-y-4">
              <p>
                We & partners use cookies, SDKs & local storage to operate
                the App, remember preferences, analytics & advertising. In-app
                privacy settings allow basic cookie controls.
              </p>
            </div>
          </section>

          {/* Section 8 - Permissions */}
          <section id="privacy-section-8" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                8
              </div>
              Permissions Justification
            </h2>
            <div className="space-y-4">
              <p>
                See Terms → Permissions. Each permission is used only for the
                specified feature; revoke in device settings.
              </p>
            </div>
          </section>

          {/* Section 9 - Security Measures */}
          <section id="privacy-section-9" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                9
              </div>
              Security Measures
            </h2>
            <div className="space-y-6">
              <p>
                We use industry standard measures (encryption in transit, access
                controls, monitoring) to protect data. No system is perfect; in
                case of a breach we will notify affected users & authorities
                per applicable law.
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h3 className="text-lg font-semibold mb-3">
                    Android Security
                  </h3>
                  <p className="mb-4">
                    Our app targets Android 15 (API level 35), incorporating the
                    latest Android security frameworks including:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-sm">
                    <li>Enhanced runtime permission controls</li>
                    <li>Improved data access transparency</li>
                    <li>Strengthened background activity limitations</li>
                    <li>Advanced app sandbox protections</li>
                    <li>
                      Modern encryption protocols for data transmission &
                      storage
                    </li>
                  </ul>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-50"
                  }`}
                >
                  <h3 className="text-lg font-semibold mb-3">
                    iOS Security
                  </h3>
                  <p className="mb-4">
                    Our iOS app targets iOS 17+ & incorporates Apple's latest security
                    frameworks including App Transport Security, keychain services, & iOS sandbox protections:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-sm">
                    <li>Enhanced runtime permission controls</li>
                    <li>Improved data access transparency</li>
                    <li>Strengthened background activity limitations</li>
                    <li>Advanced app sandbox protections</li>
                    <li>Modern encryption protocols for data transmission & storage</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 10 - Your Rights */}
          <section id="privacy-section-10" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                10
              </div>
              Your Rights
            </h2>
            <div className="space-y-4">
              <p>
                You may request access, correction, deletion, portability,
                objection to processing, & withdraw consent where applicable.
                To exercise rights, contact grievance@homeegram.com. We will
                respond within applicable legal timelines.
              </p>
            </div>
          </section>

          {/* Section 11 - Children's Privacy */}
          <section id="privacy-section-11" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                11
              </div>
              Children's Privacy
            </h2>
            <div className="space-y-4">
              <p>
                Services are for 18+. We do not knowingly collect data from
                children under 18. If you believe such data was provided,
                contact us & we will remove it.
              </p>
            </div>
          </section>

          {/* Section 12 - International Transfers */}
          <section id="privacy-section-12" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                12
              </div>
              International Transfers
            </h2>
            <div className="space-y-4">
              <p>
                Data is hosted in India; where transferred outside India we use
                safeguards required by law & notify users where required.
              </p>
            </div>
          </section>

          {/* Section 13 - App Store & Data Safety */}
          <section id="privacy-section-13" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                13
              </div>
              App Store & Data Safety
            </h2>
            <div className="space-y-6">
              <div
                className={`p-6 rounded-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <h3 className="text-lg font-semibold mb-4 text-green-400">
                  Google Play (Android)
                </h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    In-app purchases for digital goods are processed through
                    Google Play Billing; Google's refund & billing policies
                    apply.
                  </li>
                  <li>
                    We have completed the Google Play Data Safety form & the
                    entries there reflect this Privacy Policy.
                  </li>
                </ul>
              </div>

              <div
                className={`p-6 rounded-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <h3 className="text-lg font-semibold mb-4 text-blue-400">
                  Apple App Store (iOS)
                </h3>
                <div className="space-y-4">
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>
                      In-app purchases for digital goods are processed through App Store; 
                      Apple's refund & billing policies apply.
                    </li>
                    <li>
                      We have completed Apple's App Privacy details; the disclosures 
                      there match our Privacy Policy.
                    </li>
                  </ul>

                  <div className="mt-6">
                    <h4 className="font-semibold mb-3">Apple App Privacy Details Summary:</h4>
                    <div className="grid gap-4">
                      <div>
                        <h5 className="font-medium mb-2">Data Types Collected:</h5>
                        <ul className="text-sm space-y-1 ml-4">
                          <li>• Contact Info: Email, Phone, Name — Used for account management, communication, payments, & verification</li>
                          <li>• Identifiers: Device ID, User ID — Used for app functionality, analytics, & fraud prevention</li>
                          <li>• Financial Info: Payment tokens, transaction records — Used for payments & financial transactions</li>
                          <li>• Location: Approximate/Precise (optional) — Used for location-based services, mapping features, & local results</li>
                          <li>• Photos: User uploaded media, documents — Used for profiles, projects, verification, & content delivery</li>
                          <li>• User Content: Messages, posts, reviews — Used for app functionality & user communication</li>
                          <li>• Usage Data: Analytics, crash reports, search queries — Used for app improvement & functionality</li>
                          <li>• Diagnostics: Performance data, error logs — Used for app stability & performance monitoring</li>
                        </ul>
                      </div>

                      <div>
                        <h5 className="font-medium mb-2">Data Usage:</h5>
                        <ul className="text-sm space-y-1 ml-4">
                          <li>• Third-Party Advertising: Yes — only with user consent</li>
                          <li>• Developer's Advertising or Marketing: Yes — with appropriate consent</li>
                          <li>• Analytics: Yes — for app improvement & functionality</li>
                          <li>• App Functionality: Yes — core features require data processing</li>
                          <li>• Product Personalization: Yes — to customize user experience</li>
                        </ul>
                      </div>
                      
                      <div>
                        <h5 className="font-medium mb-2">Data Protection:</h5>
                        <ul className="text-sm space-y-1 ml-4">
                          <li>• Data is encrypted in transit</li>
                          <li>• Users can request account & data deletion</li>
                          <li>• Users have control over personalized advertising preferences</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`p-4 rounded-lg border-l-4 border-blue-400 ${
                  isDarkMode ? "bg-blue-900/20" : "bg-blue-50"
                }`}
              >
                <h4 className="font-semibold mb-2">iOS-Specific Data Handling</h4>
                <p className="text-sm">
                  For iOS users, certain data handling follows Apple's guidelines including 
                  App Tracking Transparency requirements for advertising identifiers & 
                  location data usage.
                </p>
              </div>
            </div>
          </section>

          {/* Section 14 - Changes to Policy */}
          <section id="privacy-section-14" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <div className="w-8 h-8 bg-[#FEF200] text-black rounded-full flex items-center justify-center mr-3 text-sm font-semibold">
                14
              </div>
              Changes to this Policy
            </h2>
            <div className="space-y-4">
              <p>
                We will update this Policy when practices change. Material
                changes will be notified in-app & via email where practicable.
              </p>
            </div>
          </section>

          {/* Additional Sections from PDF */}
          
          {/* App Store Specific Data Sharing */}
          <section className="scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <Globe className="w-8 h-8 text-[#FEF200] mr-3" />
              Platform-Specific Data Handling
            </h2>
            <div className="space-y-6">
              <div
                className={`p-6 rounded-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <h3 className="text-lg font-semibold mb-4">Data Sharing with Third Parties</h3>
                <p className="mb-4">
                  Data is shared with third parties including:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2">Core Services:</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Razorpay - Payment processing</li>
                      <li>• Ola Maps - Location services</li>
                      <li>• Sentry - Error monitoring</li>
                      <li>• Meile Search - Search functionality</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Infrastructure & Analytics:</h4>
                    <ul className="text-sm space-y-1">
                      <li>• AWS - Cloud hosting & storage</li>
                      <li>• Bunny CDN - Content delivery</li>
                      <li>• Firebase - Analytics & crash reporting</li>
                      <li>• AdMob, Meta, AppsFlyer - Advertising</li>
                    </ul>
                  </div>
                </div>
                <p className="text-sm mt-4">
                  All third-party integrations are governed by our Privacy Policy & 
                  respective data protection agreements to ensure user privacy protection.
                </p>
              </div>

              <div
                className={`p-6 rounded-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <h3 className="text-lg font-semibold mb-4">Platform Security Frameworks</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2">Android Security:</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Targets Android 15 (API level 35)</li>
                      <li>• Runtime permission controls</li>
                      <li>• App sandbox protections</li>
                      <li>• Data access transparency</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">iOS Security:</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Targets iOS 17+ frameworks</li>
                      <li>• App Transport Security</li>
                      <li>• Keychain services integration</li>
                      <li>• App Tracking Transparency compliance</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer CTA */}
        <div
          className={`mt-16 p-8 rounded-2xl text-center ${
            isDarkMode
              ? "bg-gradient-to-r from-gray-800 to-gray-700"
              : "bg-gradient-to-r from-gray-50 to-gray-100"
          }`}
        >
          <h3 className="text-xl font-semibold mb-4">
            Questions About Your Privacy?
          </h3>
          <p
            className={`mb-6 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            Our privacy team is here to help you understand & control your
            data.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() =>
                (window.location.href = "mailto:grievance@homeegram.com")
              }
              className="bg-[#FEF200] hover:bg-yellow-500 text-black font-semibold py-3 px-8 rounded-full transition-colors"
            >
              Contact Privacy Team
            </button>
            <button
              className={`border-2 font-semibold py-3 px-8 rounded-full transition-colors ${
                isDarkMode
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              Manage Privacy Settings
            </button>
          </div>
        </div>
      </main>

      <style jsx>{`
        html {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicy;